// Выбор из «Моих документов»: отдельный загруженный файл или целая папка — для шаблона и разбора документа.
// chooseDocument() открывает окно и ждёт выбора; «Загрузить с компьютера» возвращает {type: "upload"}.
import { useEffect, useRef, useState } from "react";
import { shortDate } from "../lib/text";
import { docName, foldersStore, Store, uploadedStore, useStore, type Folder, type UploadedDoc } from "../store";
import { Icon } from "./Icon";

export type Picked = { type: "doc"; doc: UploadedDoc } | { type: "folder"; folder: Folder; docs: UploadedDoc[] } | { type: "upload" };
type Options = { title: string; folders: boolean; usable?: (doc: UploadedDoc) => boolean };

const pickerStore = new Store<{ open: number; options: Options; resolve: ((picked: Picked | null) => void) | null }>(
  { open: 0, options: { title: "", folders: false }, resolve: null });

export function chooseDocument(options: Options): Promise<Picked | null> {
  pickerStore.get().resolve?.(null);
  return new Promise((resolve) => pickerStore.set({ open: pickerStore.get().open + 1, options, resolve }));
}

export function DocPicker() {
  const { open, options } = useStore(pickerStore);
  const uploaded = useStore(uploadedStore);
  const folders = useStore(foldersStore);
  const [query, setQuery] = useState("");
  const ref = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    if (open && ref.current && !ref.current.open) { setQuery(""); ref.current.showModal(); }
  }, [open]);

  const finish = (picked: Picked | null) => {
    const done = pickerStore.get().resolve;
    pickerStore.set({ ...pickerStore.get(), resolve: null });
    ref.current?.close();
    done?.(picked);
  };
  const usable = uploaded.filter((doc) => !options.usable || options.usable(doc));
  const text = query.trim().toLocaleLowerCase("ru");
  const docs = usable.filter((doc) => `${docName(doc)} ${doc.filename}`.toLocaleLowerCase("ru").includes(text));
  const folderName = new Map(folders.map((folder) => [folder.id, folder.name]));
  const groups = options.folders ? folders.map((folder) => ({ folder, docs: usable.filter((doc) => doc.folder_id === folder.id) }))
    .filter((group) => group.docs.length && group.folder.name.toLocaleLowerCase("ru").includes(text)) : [];

  return (
    <dialog ref={ref} className="picker-dialog" aria-labelledby="picker-title"
      onClose={() => { if (pickerStore.get().resolve) finish(null); }}
      onClick={(event) => { if (event.target === event.currentTarget) finish(null); }}>
      <div className="picker-head">
        <h2 id="picker-title">{options.title}</h2>
        <button type="button" className="icon-button" aria-label="Закрыть" onClick={() => finish(null)}><Icon name="close" /></button>
      </div>
      <label className="docs-search picker-search">
        <Icon name="search" />
        <input type="search" value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Поиск по документам" aria-label="Поиск по документам" />
      </label>
      <div className="picker-body">
        {groups.length > 0 && (
          <section>
            <h3>Папки целиком</h3>
            <ul className="picker-list">
              {groups.map(({ folder, docs: inside }) => (
                <li key={folder.id}>
                  <button type="button" className="picker-item" onClick={() => finish({ type: "folder", folder, docs: inside })}>
                    <span className="picker-icon is-folder"><Icon name="folder" /></span>
                    <span className="picker-text"><strong>{folder.name}</strong><span>{inside.length === 1 ? "1 документ" : `документов: ${inside.length}`}</span></span>
                  </button>
                </li>
              ))}
            </ul>
          </section>
        )}
        <section>
          {groups.length > 0 && <h3>Отдельные документы</h3>}
          <ul className="picker-list">
            {docs.map((doc) => (
              <li key={doc.id}>
                <button type="button" className="picker-item" onClick={() => finish({ type: "doc", doc })}>
                  <span className="picker-icon"><Icon name="doc" /></span>
                  <span className="picker-text">
                    <strong>{docName(doc)}</strong>
                    <span>{[doc.folder_id && folderName.get(doc.folder_id) ? `Папка «${folderName.get(doc.folder_id)}»` : "", shortDate(doc.created_at)].filter(Boolean).join(" · ")}</span>
                  </span>
                </button>
              </li>
            ))}
          </ul>
          {!docs.length && <p className="picker-empty">{usable.length ? "Ничего не найдено." : "В «Моих документах» пока нет подходящих файлов — загрузите новый."}</p>}
        </section>
      </div>
      <div className="picker-foot">
        <button type="button" className="docs-button is-primary" onClick={() => finish({ type: "upload" })}><Icon name="upload" />Загрузить с компьютера</button>
        <button type="button" className="docs-button" onClick={() => finish(null)}>Отмена</button>
      </div>
    </dialog>
  );
}
